import { computeCodeChallengeS256, generateCodeVerifier } from "./pkce";
import { storage, type TokenSet } from "./storage";
import { getEnv } from "../config/getEnv";

export type Env = {
  SF_AUTH_DOMAIN: string;
  SF_CLIENT_ID: string;
  SF_REDIRECT_URI: string;
  SF_SCOPES: string;
  SF_SILENT_SSO: boolean;
  LOGIN_HINT?: string;
};

export function readEnv(): Env {
  const d = getEnv("REACT_APP_SF_AUTH_DOMAIN") as string | undefined;
  const c = getEnv("REACT_APP_SF_CLIENT_ID") as string | undefined;
  const r = getEnv("REACT_APP_SF_REDIRECT_URI") as string | undefined;
  const s =
    (getEnv("REACT_APP_SF_SCOPES") as string | undefined) ??
    "openid api refresh_token";
  const silent =
    (getEnv("REACT_APP_SF_SILENT_SSO") as string | undefined) ?? "true";
  const loginHint = getEnv("REACT_APP_SF_LOGIN_HINT") as string | undefined;
  if (!d || !c || !r) {
    throw new Error(
      "Missing env: REACT_APP_SF_AUTH_DOMAIN, REACT_APP_SF_CLIENT_ID, REACT_APP_SF_REDIRECT_URI",
    );
  }
  return {
    SF_AUTH_DOMAIN: d.replace(/\/$/, ""),
    SF_CLIENT_ID: c,
    SF_REDIRECT_URI: r,
    SF_SCOPES: s,
    SF_SILENT_SSO: silent === "true",
    LOGIN_HINT: loginHint,
  };
}

export type AuthorizeOptions = {
  prompt?: "none" | "login" | "consent";
  login_hint?: string;
  state?: string;
};

export type CallbackResult =
  | { type: "success"; code: string; state: string }
  | { type: "error"; error: string; error_description?: string; state?: string }
  | {
      type: "unknown";
      error: string | null;
      error_description?: string;
      state?: string;
    };

export function buildAuthorizeUrl(env: Env, opts: AuthorizeOptions = {}): URL {
  const state = opts.state ?? createRandomState();
  const codeVerifier = generateCodeVerifier();
  storage.saveTemp({ state, codeVerifier, authDomain: env.SF_AUTH_DOMAIN });
  const url = new URL("/services/oauth2/authorize", env.SF_AUTH_DOMAIN);
  const params: Record<string, string> = {
    response_type: "code",
    client_id: env.SF_CLIENT_ID,
    redirect_uri: env.SF_REDIRECT_URI,
    scope: env.SF_SCOPES,
    state,
    prompt: opts.prompt ?? "login",
  };
  if (opts.prompt) params.prompt = opts.prompt;
  if (opts.login_hint) params.login_hint = opts.login_hint;
  return new URL(url.toString() + "?" + new URLSearchParams(params).toString());
}

export async function augmentAuthorizeUrlWithPkce(url: URL): Promise<URL> {
  const temp = storage.readTemp();
  if (!temp) throw new Error("Missing PKCE temp storage");
  const challenge = await computeCodeChallengeS256(temp.codeVerifier);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url;
}

export function parseCallback(currentUrl: string): CallbackResult {
  const u = new URL(currentUrl);
  const code = u.searchParams.get("code");
  const state = u.searchParams.get("state") ?? undefined;
  const err = u.searchParams.get("error");
  const errDesc = u.searchParams.get("error_description") ?? undefined;
  if (code) return { type: "success", code, state: state ?? "" };
  if (err)
    return { type: "error", error: err, error_description: errDesc, state };
  return {
    type: "unknown",
    error: null,
    error_description: "Missing code or error",
  };
}

export async function exchangeToken(env: Env, code: string): Promise<TokenSet> {
  const temp = storage.readTemp();
  if (!temp) throw new Error("Missing temp during token exchange");
  const tokenUrl = new URL(
    "/services/oauth2/token",
    env.SF_AUTH_DOMAIN,
  ).toString();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    code_verifier: temp.codeVerifier,
    client_id: env.SF_CLIENT_ID,
    redirect_uri: env.SF_REDIRECT_URI,
  });
  const resp = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
    mode: "cors",
    credentials: "include",
  });
  if (!resp.ok) throw new Error(`Token exchange failed (${resp.status})`);
  const json = (await resp.json()) as any;
  const now = Date.now();
  const tokenSet: TokenSet = {
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    id_token: json.id_token,
    instance_url: json.instance_url,
    expires_in: json.expires_in,
    issued_at: now,
  };
  storage.saveSfAuthSession(
    {
      tokenSet,
      state: temp.state,
      codeVerifier: temp.codeVerifier,
      lastAuthDomain: env.SF_AUTH_DOMAIN,
    },
    true,
  );
  storage.clearTemp();
  return tokenSet;
}

export async function refreshToken(
  env: Env,
  refreshToken: string,
): Promise<TokenSet> {
  const tokenUrl = new URL(
    "/services/oauth2/token",
    env.SF_AUTH_DOMAIN,
  ).toString();
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: env.SF_CLIENT_ID,
    refresh_token: refreshToken,
  });
  const resp = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!resp.ok) throw new Error(`Refresh failed (${resp.status})`);
  const json = (await resp.json()) as any;
  const now = Date.now();
  const current = storage.readSfAuthSession();
  const tokenSet: TokenSet = {
    access_token: json.access_token,
    refresh_token: json.refresh_token ?? refreshToken,
    id_token: json.id_token ?? current?.tokenSet?.id_token,
    instance_url: json.instance_url ?? current?.tokenSet?.instance_url,
    expires_in: json.expires_in,
    issued_at: now,
  };
  storage.saveSfAuthSession({ ...(current ?? {}), tokenSet }, true);
  return tokenSet;
}

export async function revokeToken(env: Env, token: string): Promise<void> {
  const url = new URL("/services/oauth2/revoke", env.SF_AUTH_DOMAIN).toString();
  await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ token }),
  });
}

export async function fetchUserInfo(
  env: Env,
  accessToken: string,
): Promise<any> {
  const url = new URL(
    "/services/oauth2/userinfo",
    env.SF_AUTH_DOMAIN,
  ).toString();
  const r = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!r.ok) throw new Error("userinfo_failed");
  return r.json();
}

export function createRandomState(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function shouldMirrorLocal(): boolean {
  const v = getEnv("REACT_APP_ENABLE_LOCAL_STORAGE_MIRROR") as
    | string
    | undefined;
  return v === "true";
}
